/**
 * `ProblemCard` — карточка одной проблемы хозяйства (страница «Хозяйство»,
 * блок «Проблемы хозяйства сейчас»; план §5.2). Тот же разбор, что в центре
 * «Где нужно разобраться» на Обзоре, в рамке `ui/Card`.
 *
 * API:
 *   <ProblemCard kind="deadlines" | "water" | "conflicts" | "quarantine" | "overlap"
 *                farmId={7} farmName="…" scope={ProblemScope}
 *                situation={строка очереди этого хозяйства?}
 *                districtFarms={by_farm района?} />
 *   - данные грузит сама (`useFarmReview`); карту НЕ рисует — у страницы
 *     хозяйства своя карта. Нужна карта разбора — `useFarmReview(...).map` +
 *     `<ProblemMap {...map} />`;
 *   - «Поручить уточнение» — только при ключе ситуации и праве
 *     `can_manage_situations` (как в очереди); без ситуации кнопки нет.
 * Нет проблем ни одного типа — страница рисует одну строку «Существенных
 * сигналов нет на дату X», а не пустые карточки.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarClock, Droplets, Layers, Scale, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../../auth/context';
import { assignFromSituationHref } from '../../data-source/api/assignments';
import { Card } from '../../ui/Card';
import { Icon } from '../../ui/Icon';
import type { Situation } from '../situations';
import { actionTitle, PROBLEM_LABEL, signalCounts, WHAT_TO_CHECK, type ProblemKind } from './model';
import { ReviewByKind } from './ReviewByKind';
import { SignalChips } from './SignalChips';
import { useFarmReview, type ProblemScope } from './useFarmReview';
import type { Schemas } from '../../../api/client';
import './problems.css';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';

const m = defineMessages({
  assignTitle: { id: 'board.problemCard.assignTitle', defaultMessage: '{title}: {farm}', description: 'Название поручения, создаваемого из ситуации; {title} — заголовок ситуации, {farm} — имя хозяйства' },
  assign: { id: 'board.problemCard.assign', defaultMessage: 'Поручить уточнение', description: 'Главная кнопка карточки проблемы (менеджер с правом управлять ситуациями): создать поручение; рядом стрелка' },
  check: { id: 'board.problemCard.check', defaultMessage: 'Что проверить.', description: 'Жирная подпись-вводное слово перед текстом в карточке проблемы; после неё идёт описание действия' },
});

export const KIND_ICON: Record<ProblemKind, typeof Droplets> = {
  deadlines: CalendarClock,
  water: Droplets,
  conflicts: Scale,
  quarantine: ShieldAlert,
  overlap: Layers,
};

export interface ProblemCardProps {
  kind: ProblemKind;
  farmId: number;
  farmName: string;
  scope: ProblemScope;
  situation?: Situation | null;
  districtFarms?: readonly Schemas['SeasonBreakdownRowOut'][] | null;
}

/** «Поручить уточнение» — только тому, кто вправе (см. `SituationBoard`). */
export function AssignButton({ situation }: { situation: Situation | null | undefined }) {
  const t = useT();
  const { principal } = useAuth();
  const href = situation ? assignFromSituationHref({ key: situation.key, title: t(m.assignTitle, { title: situation.title, farm: situation.name }), explanation: situation.explanation, farmId: situation.farmId }) : null;
  if (!href || !principal?.can_manage_situations) return null;
  return <Link className="astra-button astra-button-primary" to={href}>{t(m.assign)} <ArrowRight size={15} /></Link>;
}

export function ProblemCard({ kind, farmId, farmName, scope, situation, districtFarms }: ProblemCardProps) {
  const t = useT();
  const review = useFarmReview(kind, farmId, scope);
  return (
    <Card
      className="pcard"
      title={<span className="pcard__title"><Icon icon={KIND_ICON[kind]} size={18} />{actionTitle(kind, situation)}</span>}
      aside={situation ? <SignalChips counts={signalCounts(situation)} /> : t(PROBLEM_LABEL[kind])}
    >
      <p className="pcard__check"><strong>{t(m.check)}</strong> {t(WHAT_TO_CHECK[kind])}</p>
      <ReviewByKind kind={kind} review={review} scope={scope} farmId={farmId} situation={situation} districtFarms={districtFarms} />
      <div className="pcard__foot">
        <AssignButton situation={situation} />
        <span className="pcard__farm">{farmName}</span>
      </div>
    </Card>
  );
}
